import { ICodeLoader, IFluidModule } from '@fluidframework/container-definitions';
import {
  IFluidCodeDetails,
  IFluidCodeDetailsComparer,
  IProvideFluidCodeDetailsComparer
} from '@fluidframework/core-interfaces';
import { safeLocalStorage, sendTelemetryEvent } from '@fluidx/utilities';
import { defaultAllowedCodeOrigins } from './defaultAllowedCodeOrigins';
import {
  getCh5ContainerCodeDetailsProvider,
  getOfficeContainerCodeDetailsProvider,
  getCatalogContainerCodeDetailsProvider
} from './FFXCodeDetailsProviders';
import {
  Ch5ContainerPackageName,
  OfficeFluidContainerPackageName,
  OneNoteMeetingPackageName,
  CatalogContainerPackageName
} from './Constants';
import {
  ContainerCodeDetailsProvider,
  BohemiaCodeLoaderConfig,
  IVersionedFluidBrowserPackage,
  ActualCodeDetailsGetter
} from './types';
import { SimpleCodeLoader } from './SimpleCodeLoader';
import { ITelemetryBaseLogger } from '@fluidframework/common-definitions';
import { GenericEvent, GenericEventSubCategory } from './telemetry/GenericEvent';

function getAllowListOverride(logger?: ITelemetryBaseLogger): string[] {
  const allowListOverride = safeLocalStorage()?.AllowListOverride;
  if (allowListOverride) {
    // Log a generic event to correlate QoS telemetry with dev-flow scenarios
    // Task 4544147: Consider adding a helper method for the same
    logger &&
      sendTelemetryEvent(logger, {
        eventName: GenericEvent.CodeLoaderAllowListOverwritten,
        subCategory: GenericEventSubCategory.LocalStorageOverride
      });
    return JSON.parse(allowListOverride);
  }

  return [];
}

// Experimental code loader that has the goal of being more generic than the current Bohemia code loader.
export class BohemiaCodeLoader implements ICodeLoader, IProvideFluidCodeDetailsComparer, ActualCodeDetailsGetter {
  private readonly simpleCodeLoader: SimpleCodeLoader;
  private readonly containerCodeDetailsProviderMap: Map<string, ContainerCodeDetailsProvider>;
  private readonly logger?: ITelemetryBaseLogger;

  public get IFluidCodeDetailsComparer(): IFluidCodeDetailsComparer {
    return this.simpleCodeLoader;
  }

  public getActualCodeDetails(codeDetails: IFluidCodeDetails): IFluidCodeDetails | undefined {
    return this.simpleCodeLoader.getActualCodeDetails(codeDetails);
  }

  constructor(private config: BohemiaCodeLoaderConfig) {
    const additionalCodeOrigins = config.additionalAllowedCodeOrigins ?? [];

    this.logger = config.logger;

    const officeContainerCodeDetailsProvider = getOfficeContainerCodeDetailsProvider(
      this.config.logger,
      this.config.localeHint
    );

    const ch5ContainerCodeDetailsProvider = getCh5ContainerCodeDetailsProvider(
      this.config.logger,
      this.config.localeHint
    );

    const catalogContainerCodeDetailsProvider = getCatalogContainerCodeDetailsProvider(
      this.config.logger,
      this.config.localeHint
    );

    this.containerCodeDetailsProviderMap = new Map([
      [OfficeFluidContainerPackageName, officeContainerCodeDetailsProvider],
      [OneNoteMeetingPackageName, officeContainerCodeDetailsProvider],
      [Ch5ContainerPackageName, ch5ContainerCodeDetailsProvider],
      [CatalogContainerPackageName, catalogContainerCodeDetailsProvider]
    ]);

    config.additionalCodeDetailsProviders?.forEach(([packageName, codeProvider]) =>
      this.containerCodeDetailsProviderMap.set(packageName, codeProvider)
    );

    const allowedCodeOrigins = [
      ...defaultAllowedCodeOrigins,
      ...getAllowListOverride(config.logger),
      ...additionalCodeOrigins
    ];

    this.simpleCodeLoader = new SimpleCodeLoader({
      getFluidCodeDetailsPackageOverride: async (packageName) => {
        // Check if any containerCodeDetailProviders have been specified for the given package
        const codeDetailsProvider = this.containerCodeDetailsProviderMap.get(packageName);
        if (codeDetailsProvider) {
          return codeDetailsProvider.getCodeDetailsPackage(config.containerEnvironment, allowedCodeOrigins);
        }
        return undefined;
      },
      allowedCodeOrigins,
      getFluidCodeVersionDetails: (fluidPackage: IVersionedFluidBrowserPackage) => {
        const { containerEnvironment } = config;

        const version = fluidPackage.version;
        const upgradeNeeded = this.containerCodeDetailsProviderMap.get(fluidPackage.name)?.upgradeNeeded;

        return {
          version,
          containerEnvironment,
          // If no upgradeNeeded function is provided, provide a function that always returns false
          needUpgrade: upgradeNeeded ? () => upgradeNeeded(containerEnvironment, version) : async () => false
        };
      }
    });
  }

  public async preCache(packageName: string): Promise<void> {
    const containerCodeDetailsProvider = this.containerCodeDetailsProviderMap.get(packageName);

    if (containerCodeDetailsProvider && containerCodeDetailsProvider.preCacheLatestContainerCode) {
      return containerCodeDetailsProvider.preCacheLatestContainerCode(this.config.containerEnvironment);
    }

    return this.simpleCodeLoader.preCache(packageName);
  }

  public async load<T extends IFluidModule>(fluidCodeDetails: IFluidCodeDetails): Promise<T> {
    return this.simpleCodeLoader.load(fluidCodeDetails, this.logger);
  }
}
